/**
 * Outcome Driver Framework - Complete Enhanced Entry
 * Type 2 (Clarity & Alignment) framework
 * 
 * Core Question: "What's the compelling reason for change?"
 */

import type { EnhancedFrameworkEntry } from '../../types/enhancedFramework';

export const outcomeDriverFramework: EnhancedFrameworkEntry = {
  // ===== BASIC IDENTITY =====
  id: 'outcome-driver',
  canonicalName: 'Outcome Driver',
  aliases: ['Driver', 'Compelling Reason for Change', 'Change Driver'],
  
  // ===== TYPE CLASSIFICATION =====
  frameworkType: 'clarity-alignment',
  
  // ===== MULTI-RESOLUTION CONTENT =====
  content: {
    phrase: 'A compelling reason for change.',
    
    sentence: 'An Outcome Driver spells out the need for something to be different by combining current state, problem/opportunity, and impact into a succinct explanation that motivates people to either move away from something negative or toward greater possibility.',
    
    paragraph: 'Unlike a problem statement or definition, an Outcome Driver is a compelling reason to invest in change. It establishes the link between a current state and the need or motivation for change, gesturing toward possibilities for a better world. Each driver has three parts: (1) Current State (factual truth), (2) Problem/Opportunity Statement (the gap without value judgment), and (3) Value Judgment/Impact (why it matters). This structure creates urgency and clarity, setting up the Desired Outcome that follows.',
    
    fullPage: `# Outcome Driver Framework

## The Core Question

**"What's the compelling reason for change?"**

## Overview

An **Outcome Driver** is a compelling reason for change. It spells out the need for something to be different. Unlike a problem statement or definition, an Outcome Driver is a succinct explanation of the reason people either would want to **get away from something negative** and/or **go toward a greater sense of possibility**.

## Why Outcome Drivers Matter

Outcome Drivers are important because they:
- **Establish the link** between a current state and the need or motivation for change
- **Gesture toward possibilities** for a better world
- **Link directly to a Desired Outcome** (the "what we want" that follows the "why we need change")
- **Create urgency and clarity** before investing resources
- **Align stakeholders** on the problem/opportunity before jumping to solutions

In a nutshell: **A driver is a compelling reason to invest in change.**

## The Three-Part Structure

Outcome Drivers express the motive or reasons to leave the status quo and make a change—to move away from something less desirable or toward something better.

### Formula

**Current State + Problem/Opportunity Statement + Value Judgment/Impact = Outcome Driver**

### The Three Parts

#### 1. Current State
**[Just factual truths]**

A short statement of what happens to be true. No value judgments. No emotion. Just facts.

**Examples:**
- "I travel a lot and eat out almost every meal for business, typically on the expensive side."
- "Book is on track for Q2 2021 launch."
- "Our customer support team responds to tickets within 48 hours."

#### 2. Problem/Opportunity Statement
**[What is the gap between current and desired state without value judgments]**

A succinct description of the essence of the matter. The gap between what is and what could/should be.

**Frame it as:**
- "Here's how it's a problem..."
- OR "Here's what the opportunity is..."

**Examples:**
- "I observe myself eating far too much and often eating the wrong kinds of foods, whether or not they're part of my strict diet."
- "Window for marketing and creating demand in advance of publication is closing."
- "Customers expect same-day responses; competitors respond within 4 hours."

#### 3. Value Judgment / Impact
**[Why is the problem bad or the opportunity good]**

A brief assertion of the effect. This is where you add the "so what?"—the consequence, impact, or value judgment that makes it compelling.

**Frame it as:**
- "It's a problem because..."
- OR "It's an opportunity because..."

**Examples:**
- "This is bad for my weight, the drinks foul up my willpower, and the sugar is addictive. Ultimately, it dulls my mind and makes me feel bad about myself."
- "Huge and unrecoverable opportunity cost and revenue loss is unconscionable."
- "We're losing customers to competitors who respond faster, costing us $X in churn."

## Example: Executive Coaching Client

**Current State:**
I travel a lot and eat out almost every meal for business, typically on the expensive side. More often than not, my clients pick up the tab. We order appetizers, drinks, a main course, and often dessert.

**Problem Statement:**
I observe myself eating far too much and often eating the wrong kinds of foods and ingredients, whether or not they're part of my strict diet.

**Value Judgment/Impact:**
This is a serious problem because it's bad for my weight, the drinks foul up my willpower, and the sugar is addictive. Ultimately, it dulls my mind and makes me feel bad about myself.

**What it points toward (Desired Outcome):**
I need to be more disciplined, develop better habits, and learn to say "no" politely.

## How Outcome Driver Leads to Desired Outcome

The Outcome Driver creates the **"WHY"** for change. It establishes urgency and motivation.

The Desired Outcome Framework then answers:
- **WHO** wants the change (often revealed by the driver)
- **WHAT** they want specifically (hinted at in the driver)
- **HOW** they'll know they have it (evidence)
- **WHAT** obstacles stand in the way
- **WHAT** steps to take

**Flow:** Outcome Driver → Desired Outcome → Simple Strategy → Execution

## Multiple Drivers

Often, you'll have multiple Outcome Drivers for a single initiative. Each one adds weight to the case for change.

**Example:**

| Driver | Current State | Problem/Opportunity | Impact |
|--------|--------------|---------------------|--------|
| #1 | Book is on track for Q2 2021 launch | Window for marketing and creating demand in advance of publication is closing | Huge and unrecoverable opportunity cost and revenue loss is unconscionable |
| #2 | Competing book announced for Q1 2021 | We lose first-mover advantage in this space | Market will perceive us as followers, not leaders; sales will suffer |
| #3 | Publishing team is at capacity with other launches | No dedicated resources for pre-launch marketing | Launch will fail to achieve target sales without proper promotion |

Each driver reinforces the urgency from a different angle.

## When to Use Outcome Drivers

Use Outcome Drivers when:
- **Starting any initiative** - Clarify WHY before WHAT
- **Building a business case** - Stack multiple drivers for compelling justification
- **Aligning stakeholders** - Get agreement on the problem/opportunity before solutions
- **Prioritizing requests** - Compare drivers to determine urgency and impact
- **Executive presentations** - Lead with the driver to create urgency
- **Change management** - Help people understand why change is needed

## Common Mistakes

1. **Skipping straight to solutions** - Don't jump to "what we should do" before establishing "why we need change"
2. **Value judgments in Current State** - Keep Part 1 factual only
3. **Weak or missing Impact** - Part 3 must be compelling; don't be timid
4. **Conflating problem with solution** - State the gap, not the fix
5. **Too vague** - Be specific and concrete in all three parts`
  },
  
  // ===== RELEVANCE FRAMEWORK =====
  relevance: {
    what: 'A three-part structure (Current State + Problem/Opportunity + Impact) that creates a compelling reason for change by establishing the link between where we are and why we need to be somewhere different.',
    
    why: 'Before investing in change, people need to understand WHY change is necessary. Outcome Drivers create urgency and clarity by showing the gap between current and desired state, plus the consequences of inaction or benefits of action. Without a clear driver, initiatives lack motivation and stakeholder alignment.',
    
    how: 'By articulating three distinct parts: (1) Current State (factual truth), (2) Problem/Opportunity Statement (the gap without value judgment), and (3) Value Judgment/Impact (why it matters). This structure separates fact from interpretation, making the driver both objective and compelling. Multiple drivers can be stacked to build a stronger case for change.'
  },
  
  // ===== FRAMEWORK STRUCTURE =====
  structure: {
    components: [
      {
        name: 'Current State',
        description: 'Factual truth about what is currently happening',
        purpose: 'Establish objective reality without bias or judgment. This is the baseline from which change will be measured.'
      },
      {
        name: 'Problem/Opportunity Statement',
        description: 'The gap between current and desired state',
        purpose: 'Articulate what\'s wrong (problem) or what could be better (opportunity) without adding value judgments. This is the "what" of the gap.'
      },
      {
        name: 'Value Judgment/Impact',
        description: 'Why the problem is bad or the opportunity is good',
        purpose: 'Make it compelling. Add the consequence, impact, or value judgment that creates urgency. This is the "so what?" that motivates action.'
      }
    ],
    questions: [
      {
        number: 1,
        question: 'What is factually true right now? (Current State)',
        intent: 'Establish objective baseline without value judgments.',
        guidance: 'State only facts. Avoid words like "unfortunately," "sadly," "great," "bad." Just describe what IS.',
        examples: [
          '✓ "I travel a lot and eat out almost every meal for business."',
          '✓ "Book is on track for Q2 2021 launch."',
          '✓ "Our support team responds to tickets within 48 hours."',
          '✗ "I unfortunately travel too much" (value judgment)',
          '✗ "Our slow support team..." (judgment embedded)'
        ]
      },
      {
        number: 2,
        question: 'What is the gap between current and desired state? (Problem/Opportunity)',
        intent: 'Describe the essence of what\'s wrong or what could be better—without yet saying why it matters.',
        guidance: 'Frame as "Here\'s how it\'s a problem..." or "Here\'s what the opportunity is..." Stay descriptive, not evaluative.',
        examples: [
          '✓ "I observe myself eating far too much and often the wrong kinds of foods."',
          '✓ "Window for marketing in advance of publication is closing."',
          '✓ "Customers expect same-day responses; competitors respond within 4 hours."',
          '✗ "This terrible eating pattern..." (jumped to judgment)',
          '✗ "We stupidly didn\'t plan marketing..." (blame, not gap)'
        ]
      },
      {
        number: 3,
        question: 'Why does this gap matter? What\'s the impact? (Value Judgment/Impact)',
        intent: 'Make it compelling. Add the consequence or benefit that creates urgency.',
        guidance: 'Frame as "It\'s a problem because..." or "It\'s an opportunity because..." Be specific about impact—financial, emotional, strategic, competitive.',
        examples: [
          '✓ "Bad for my weight, fouls up my willpower, dulls my mind, makes me feel bad about myself."',
          '✓ "Huge and unrecoverable opportunity cost and revenue loss is unconscionable."',
          '✓ "We\'re losing customers to competitors, costing us $X/month in churn."',
          '✗ "It\'s not ideal" (too weak)',
          '✗ "Some people might not like it" (vague, lacks urgency)'
        ]
      }
    ]
  },
  
  // ===== RELATIONSHIPS =====
  relationships: {
    precededBy: [],
    followedBy: [
      {
        frameworkId: 'desired-outcome',
        description: 'The Outcome Driver creates the WHY for change (compelling reason). The Desired Outcome Framework then answers: WHO wants it? WHAT do they want? HOW will they know they have it? The driver establishes urgency and motivation; the Desired Outcome provides clarity on the target state.'
      }
    ],
    partOf: [],
    relatedTo: [
      {
        frameworkId: 'relevance-framework',
        relationshipType: 'complements',
        description: 'The Outcome Driver uses a three-part structure (Current/Problem/Impact) similar to the Relevance Framework\'s What/Why/How structure. Both separate facts from interpretation.'
      },
      {
        frameworkId: 'simple-strategy',
        relationshipType: 'prerequisite',
        description: 'Before defining strategy (where to play, how to win), you need clarity on WHY change is needed. Outcome Drivers provide this foundation.'
      }
    ],
    usedIn: [
      {
        frameworkId: 'pf.degap.stages',
        stage: 'Define',
        purpose: 'Outcome Drivers are used at the Define stage to establish the problem/opportunity before engaging stakeholders. They create the urgency that justifies the initiative.'
      }
    ]
  },
  
  // ===== SCOPE & APPLICABILITY =====
  scope: {
    levels: ['individual', 'team', 'organization'],
    contexts: [
      'Starting any new initiative or project',
      'Building business cases',
      'Stakeholder alignment meetings',
      'Executive presentations',
      'Change management communications',
      'Prioritization exercises',
      'Personal goal setting (establishing why you want change)',
      'Product requirements (establishing market need)',
      'Grant proposals',
      'Investment pitches'
    ],
    stages: [
      {
        stage: 'Before planning',
        purpose: 'Establish WHY change is needed before defining WHAT to do'
      },
      {
        stage: 'Stakeholder alignment',
        purpose: 'Get agreement on the problem/opportunity before proposing solutions'
      },
      {
        stage: 'Prioritization',
        purpose: 'Compare drivers across initiatives to determine urgency and impact'
      }
    ]
  },
  
  // ===== PRACTICAL RESOURCES =====
  resources: {
    templates: [
      {
        id: 'single-driver',
        name: 'Single Outcome Driver Template',
        type: 'worksheet',
        description: 'Template for articulating one outcome driver',
        content: `# Outcome Driver

## Current State
[Just factual truths - no value judgments]



## Problem / Opportunity Statement
[What is the gap between current and desired state without value judgments]

Here's how it's a problem...
OR
Here's what the opportunity is...



## Value Judgment / Impact
[Why is the problem bad or the opportunity good]

It's a problem because...
OR
It's an opportunity because...



## Points Toward (Optional)
[What Desired Outcome does this driver suggest?]

`
      },
      {
        id: 'multiple-drivers',
        name: 'Multiple Drivers Template',
        type: 'worksheet',
        description: 'Template for stacking multiple drivers',
        content: `# Outcome Drivers for [Initiative Name]

| Driver | Current State | Problem/Opportunity | Value Judgment/Impact |
|--------|--------------|---------------------|----------------------|
| #1 | | | |
| #2 | | | |
| #3 | | | |

## Collective Weight
[How do these drivers together create urgency for change?]

`
      }
    ],
    
    facilitationGuides: [
      {
        title: 'Outcome Driver Workshop',
        duration: 45,
        roles: ['Facilitator', 'Stakeholders (decision-makers)', 'Scribe'],
        materials: ['Driver template', 'Whiteboard', 'Sticky notes'],
        steps: [
          {
            stepNumber: 1,
            title: 'Frame the exercise',
            duration: 5,
            instructions: 'Explain that we\'re establishing WHY change is needed before discussing WHAT to do. Show the three-part structure.',
            tips: [
              'Emphasize: We\'re not solving yet, just clarifying the problem/opportunity',
              'Show an example driver to illustrate the structure'
            ]
          },
          {
            stepNumber: 2,
            title: 'Current State (facts only)',
            duration: 10,
            instructions: 'Have participants state factual truths about the current situation. Push back on value judgments. Capture on sticky notes.',
            tips: [
              'Ask: "What is objectively true right now?"',
              'When someone says "unfortunately" or "sadly," remove it',
              'If they say "bad" or "slow," ask for the factual measure'
            ]
          },
          {
            stepNumber: 3,
            title: 'Problem/Opportunity (the gap)',
            duration: 15,
            instructions: 'Have participants describe the gap between current and desired. Still no value judgments—just the delta.',
            tips: [
              'Frame as: "Here\'s how it\'s a problem..." or "Here\'s the opportunity..."',
              'Push for specificity: What exactly is the gap?',
              'Multiple gaps might emerge—that\'s fine, they become separate drivers'
            ]
          },
          {
            stepNumber: 4,
            title: 'Value Judgment/Impact (make it compelling)',
            duration: 10,
            instructions: 'Now add the "so what?"—why does this gap matter? What\'s the consequence or benefit? Be specific.',
            tips: [
              'Ask: "What happens if we do nothing?"',
              'Probe for impact: Financial? Competitive? Emotional? Strategic?',
              'Don\'t accept weak language like "not ideal"—push for urgency'
            ]
          },
          {
            stepNumber: 5,
            title: 'Synthesize and prioritize',
            duration: 5,
            instructions: 'If multiple drivers emerged, prioritize them. Which are most compelling? Stack them for cumulative weight.',
            tips: [
              'Drivers can reinforce each other',
              'Lead with the strongest driver in presentations',
              'Keep all drivers documented for reference'
            ]
          }
        ],
        commonPitfalls: [
          'Jumping to solutions before establishing the driver',
          'Mixing value judgments into Current State',
          'Weak or vague Impact statements',
          'Confusing the problem with the solution'
        ]
      }
    ],
    
    instructions: {
      individual: `# Using Outcome Driver Solo

1. **Start with facts**: Write down what is objectively true about the current situation. No judgments.

2. **Describe the gap**: What\'s the difference between current and desired state? Frame as problem or opportunity.

3. **Add impact**: Why does this gap matter? What\'s the consequence of inaction or benefit of action?

4. **Check your work**:
   - Part 1: Could someone dispute these facts? If yes, refine to be more objective.
   - Part 2: Did you describe the gap without saying why it\'s bad/good? If not, move judgments to Part 3.
   - Part 3: Is the impact compelling? Would it motivate someone to act? If not, dig deeper.

5. **Consider multiple drivers**: You may have several reasons for change. Write each as a separate driver.

6. **Point toward the outcome**: Once your driver is clear, it will suggest a Desired Outcome. "I need to..." becomes the starting point for the Desired Outcome Framework.`,
      
      team: `# Using Outcome Driver with a Team

**Before the meeting:**
- Share the three-part structure
- Ask participants to come with initial thoughts on current state

**During the meeting:**
- Use the facilitation guide (45 minutes)
- Capture drivers on a shared template
- Get explicit agreement on facts (Part 1) before moving to gap (Part 2)
- Make Part 3 as compelling as possible—this creates urgency

**After the meeting:**
- Document all drivers
- Use the strongest driver(s) in presentations and business cases
- Feed directly into a Desired Outcome session
- Reference drivers when making priority decisions`
    },
    
    criticalCallouts: [
      {
        id: 'why-before-what',
        type: 'requirement',
        title: 'WHY Before WHAT',
        content: 'Outcome Drivers establish WHY change is needed before you define WHAT to do. Without a clear driver, initiatives lack urgency and motivation. Start here.',
        icon: '❓'
      },
      {
        id: 'facts-vs-judgments',
        type: 'requirement',
        title: 'Separate Facts from Judgments',
        content: 'Current State must be factual only. Save value judgments for Part 3 (Impact). This separation makes the driver both objective and compelling.',
        icon: '📊'
      },
      {
        id: 'compelling-impact',
        type: 'requirement',
        title: 'Make Impact Compelling',
        content: 'Part 3 (Impact) must create urgency. Don\'t be timid. Be specific about consequences or benefits. Weak impact = weak driver = low priority.',
        icon: '💥'
      },
      {
        id: 'multiple-drivers',
        type: 'tip',
        title: 'Stack Multiple Drivers',
        content: 'Often you\'ll have several reasons for change. Each driver adds weight. Stack them for cumulative urgency. Lead with the strongest in presentations.',
        icon: '📚'
      },
      {
        id: 'leads-to-outcome',
        type: 'tip',
        title: 'Points Toward Desired Outcome',
        content: 'The driver creates the WHY. The Desired Outcome Framework then defines WHO, WHAT, HOW. Use them in sequence: Driver → Desired Outcome → Strategy → Execution.',
        linkedConcept: 'desired-outcome',
        icon: '➡️'
      }
    ],
    
    media: []
  },
  
  // ===== LEARNING INTEGRATION =====
  learning: {
    frameworkType: 'clarity-alignment',
    difficulty: 1,
    timeToLearn: 15,
    prerequisites: [],
    practiceExercises: [
      {
        title: 'Personal Driver Exercise',
        description: 'Think of a change you want to make in your life. Write an Outcome Driver using the three-part structure. Be brutally honest in Part 3 (Impact).',
        duration: 15,
        difficulty: 1
      },
      {
        title: 'Business Case Driver',
        description: 'Take a current initiative at work. Write 3 separate drivers for it. See how stacking them creates stronger urgency.',
        duration: 20,
        difficulty: 2
      },
      {
        title: 'Critique a Weak Driver',
        description: 'Find a weak business case or proposal. Identify where the driver is missing or weak. Rewrite it using the three-part structure.',
        duration: 15,
        difficulty: 2
      }
    ]
  },
  
  // ===== VISUAL LANGUAGE =====
  visual: {
    primaryDiagram: {
      type: 'flow',
      title: 'From Driver to Outcome',
      description: 'Shows the three-part structure of Outcome Driver (Current → Gap → Impact) leading to Desired Outcome',
      svgComponent: 'OutcomeDriverFlowDiagram',
      interactive: false,
      altText: 'Flow diagram showing Current State + Problem/Opportunity + Impact = Driver, which points to Desired Outcome'
    },
    icon: '🚀',
    color: '#f5b969',  // amber-500 (brand color)
    metaphor: {
      family: 'journey',
      phrase: 'The fuel that powers the expedition',
      visualCues: ['🚀 Driver', '⚡ Urgency', '🎯 Points Toward', '💨 Momentum']
    },
    onBrandElements: {
      backgroundColor: '#fef8f0',      // amber-50
      accentColor: '#f5b969',          // amber-500
      borderStyle: '2px solid #f9d5a5',  // amber-300
      shadowStyle: '0 4px 6px rgba(245, 185, 105, 0.1)'
    }
  },
  
  // ===== QUALITY & MISSTEPS =====
  quality: {
    checklist: {
      items: [
        {
          criterion: 'Current State is factual only (no value judgments)',
          passFail: true,
          guidance: 'Could someone reasonably dispute these facts? If yes, refine to be more objective.'
        },
        {
          criterion: 'Problem/Opportunity describes the gap without saying why it matters',
          passFail: true,
          guidance: 'Did you save the "so what?" for Part 3? Part 2 should describe the delta, not evaluate it.'
        },
        {
          criterion: 'Value Judgment/Impact is compelling and specific',
          passFail: true,
          guidance: 'Would this create urgency for action? Is the impact clear (financial, competitive, emotional, strategic)?'
        },
        {
          criterion: 'Driver points toward a clear Desired Outcome',
          passFail: true,
          guidance: 'Does the driver suggest WHO wants change and WHAT they might want?'
        },
        {
          criterion: 'Multiple drivers considered (if applicable)',
          passFail: true,
          guidance: 'Are there other compelling reasons for change? Stack them for cumulative weight.'
        }
      ]
    },
    commonMissteps: {
      missteps: [
        {
          mistake: 'Skipping straight to solutions',
          consequence: 'No urgency established; stakeholders don\'t understand WHY change is needed; initiative lacks momentum',
          fix: 'Always start with the driver. Establish the compelling reason for change before proposing solutions.',
          relatedQuestion: 'All parts'
        },
        {
          mistake: 'Value judgments in Current State',
          consequence: 'Facts become subjective; stakeholders can dispute the baseline; driver loses credibility',
          fix: 'Remove all evaluative language from Part 1. Just state what IS. Save judgments for Part 3.',
          relatedQuestion: 'Part 1: Current State'
        },
        {
          mistake: 'Weak or missing Impact',
          consequence: 'Driver lacks urgency; initiative gets deprioritized; stakeholders not motivated to act',
          fix: 'Be specific and bold in Part 3. What\'s the real consequence or benefit? Don\'t be timid. Create urgency.',
          relatedQuestion: 'Part 3: Value Judgment/Impact'
        },
        {
          mistake: 'Conflating problem with solution',
          consequence: 'Confuses what\'s wrong with how to fix it; limits creative problem-solving',
          fix: 'In Part 2, describe the gap—what\'s different between current and desired. Don\'t prescribe the fix.',
          relatedQuestion: 'Part 2: Problem/Opportunity'
        },
        {
          mistake: 'Too vague or abstract',
          consequence: 'Driver doesn\'t resonate; stakeholders can\'t relate; lacks emotional impact',
          fix: 'Be concrete and specific in all three parts. Use real numbers, real examples, real consequences.',
          relatedQuestion: 'All parts'
        },
        {
          mistake: 'Only one driver when multiple exist',
          consequence: 'Underweight the case for change; miss opportunities to reinforce from different angles',
          fix: 'Look for multiple drivers. Financial + competitive + strategic + emotional. Stack them.',
          relatedQuestion: 'All parts'
        }
      ]
    }
  },
  
  // ===== METADATA =====
  metadata: {
    createdDate: '2024-01-01',
    lastUpdated: '2025-10-01',
    version: '1.0',
    status: 'published',
    contributors: ['Peak Priorities Team'],
    tags: [
      'clarity',
      'alignment',
      'change-management',
      'business-case',
      'motivation',
      'urgency',
      'problem-statement',
      'type-2',
      'thinking-framework'
    ]
  }
};

